import React, { useCallback, useMemo } from 'react';
import {
  Accordion, Col, Row,
} from 'react-bootstrap';
import Checkbox from 'components/Form/FormCheckbox';
import CardLight from 'components/CardLight';
import { useTranslation } from 'react-i18next';
import { DocRequestTemplateCategoryType } from 'types/DocRequestTemplate';
import { DocRequestEntity, DocRequestGrouped } from 'types/DocRequest';
import _ from 'lodash';
import useDownloadDocRequest from 'hooks/useDownloadDocRequest';
import PdfViewerModal from 'components/Modal/PdfViewerModal';
import BtnCreateRequest from 'components/DocsManagment/BtnCreateRequest';
import DocsTableEdit from '../TableEdit';

type DocsViewProps = {
  data?: DocRequestGrouped
  fetchData: () => void
  setItem: ( newData: DocRequestEntity ) => void
  toggleMassiveActive: ( docsToUpdate: DocRequestEntity[], newValue: boolean ) => Promise<void>
};

function DocsView( {
  data, fetchData, setItem, toggleMassiveActive,
}:DocsViewProps ) {
  const { t } = useTranslation();

  const {
    closeFileModal, currentFileUrl, handleViewDocument, showFileModal, clearCachedFile,
  } = useDownloadDocRequest();

  const reloadData = useCallback( ( newData: DocRequestEntity ) => {
    setItem( newData );
    clearCachedFile( newData.id );
  }, [clearCachedFile] );

  const contacts = useMemo( () => _.groupBy( data?.[DocRequestTemplateCategoryType.physical], 'contactoID' ), [data] );

  const titularDocs = data?.[DocRequestTemplateCategoryType.titular] || [];
  const isTitularActive = useMemo(
    () => !!titularDocs.length && titularDocs.some( ( doc ) => doc.activo ),
    [titularDocs],
  );
  const handleToggleTitular = useCallback( ( newValue: boolean ) => {
    toggleMassiveActive( titularDocs, newValue );
  }, [titularDocs, toggleMassiveActive] );

  const fiscalDocs = data?.[DocRequestTemplateCategoryType.fiscal] || [];
  const isFiscalActive = useMemo(
    () => !!fiscalDocs.length && fiscalDocs.some( ( doc ) => doc.activo ),
    [fiscalDocs],
  );
  const handleToggleFiscal = useCallback( ( newValue: boolean ) => {
    toggleMassiveActive( fiscalDocs, newValue );
  }, [fiscalDocs, toggleMassiveActive] );

  const investmentDocs = data?.[DocRequestTemplateCategoryType.investments] || [];
  const isInvestmentActive = useMemo(
    () => !!investmentDocs.length && investmentDocs.some( ( doc ) => doc.activo ),
    [investmentDocs],
  );
  const handleToggleInvestment = useCallback( ( newValue: boolean ) => {
    toggleMassiveActive( investmentDocs, newValue );
  }, [investmentDocs, toggleMassiveActive] );

  const handleToggleContact = useCallback( ( docs: DocRequestEntity[], newValue: boolean ) => {
    toggleMassiveActive( docs, newValue );
  }, [toggleMassiveActive] );

  return (
    <div className="table-docs">
      <CardLight>
        {!data?.[DocRequestTemplateCategoryType.physical]?.length && (
        <>
          <Row className="mb-3">
            <Col sm>
              <h4 className="text-ellipsis">{t( 'admin.docs.physicalPerson' )}</h4>
            </Col>
          </Row>
          <h4 className="text--gray12 text-center">{t( 'admin.docs.empty' )}</h4>
          <BtnCreateRequest
            category={DocRequestTemplateCategoryType.physical}
            reloadData={fetchData}
          />
        </>
        )}
        {!!data?.[DocRequestTemplateCategoryType.physical]?.length && (
          <>
            <Row>
              <Col sm>
                <h4 className="text-ellipsis">{t( 'admin.docs.physicalPerson' )}</h4>
              </Col>
              {/* <Col sm="auto">
                <Checkbox
                  showErrors={false}
                  type="switch"
                  classNames={{
                    formGroup: 'm-0 me-1',
                  }}
                />
              </Col> */}
            </Row>

            <Accordion
              className="mt-0"
              defaultActiveKey={Object.keys( contacts )?.[0]}
              key={Object.keys( contacts )?.[0]}
              alwaysOpen
            >
              {Object.keys( contacts )?.map( ( item ) => {
                const contactDocs = contacts[item] || [];
                const isContactActive = contactDocs.some( ( doc ) => doc.activo );
                return (
                  <Accordion.Item eventKey={item} key={item}>
                    <Accordion.Header>
                      <Row className="w-100 gx-0">
                        <Col sm>
                          {contacts[item]?.[0].contactoName}
                        </Col>
                        <Col sm="auto" onClick={( e ) => e.stopPropagation()}>
                          <Checkbox
                            showErrors={false}
                            type="switch"
                            value={isContactActive}
                            onChange={(
                              newValue: boolean,
                            ) => handleToggleContact( contactDocs, newValue )}
                            classNames={{
                              formGroup: 'm-0',
                            }}
                          />
                        </Col>
                      </Row>
                    </Accordion.Header>
                    <Accordion.Body className="pe-0">
                      <DocsTableEdit
                        data={contacts[item]}
                        reloadData={reloadData}
                        handleViewDocument={handleViewDocument}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                );
              } )}
            </Accordion>

            <BtnCreateRequest
              category={DocRequestTemplateCategoryType.physical}
              reloadData={fetchData}
            />
          </>
        )}
      </CardLight>

      <CardLight extraClass="mt-3">
        <Row className="mb-3">
          <Col sm>
            <h4 className="text-ellipsis">{t( 'admin.docs.titular' )}</h4>
          </Col>
          <Col sm="auto">
            <Checkbox
              showErrors={false}
              type="switch"
              value={isTitularActive}
              onChange={handleToggleTitular}
              classNames={{
                formGroup: 'm-0 me-1',
              }}
            />
          </Col>
        </Row>

        {data?.[DocRequestTemplateCategoryType.titular]?.length
          ? (
            <DocsTableEdit
              data={data?.[DocRequestTemplateCategoryType.titular]}
              reloadData={reloadData}
              handleViewDocument={handleViewDocument}
            />
          )
          : <h4 className="text--gray12 text-center">{t( 'admin.docs.empty' )}</h4>}
        <BtnCreateRequest
          category={DocRequestTemplateCategoryType.titular}
          reloadData={fetchData}
        />
      </CardLight>

      <CardLight extraClass="mt-3">
        <Row className="mb-3">
          <Col sm>
            <h4 className="text-ellipsis">{t( 'admin.docs.fiscalPerson' )}</h4>
          </Col>
          <Col sm="auto">
            <Checkbox
              showErrors={false}
              type="switch"
              value={isFiscalActive}
              onChange={handleToggleFiscal}
              classNames={{
                formGroup: 'm-0 me-1',
              }}
            />
          </Col>
        </Row>

        {data?.[DocRequestTemplateCategoryType.fiscal]?.length
          ? (
            <DocsTableEdit
              data={data?.[DocRequestTemplateCategoryType.fiscal]}
              reloadData={reloadData}
              handleViewDocument={handleViewDocument}
            />
          )
          : <h4 className="text--gray12 text-center">{t( 'admin.docs.empty' )}</h4>}
        <BtnCreateRequest
          category={DocRequestTemplateCategoryType.fiscal}
          reloadData={fetchData}
        />
      </CardLight>

      <CardLight extraClass="mt-3">
        <Row className="mb-3">
          <Col sm>
            <h4 className="text-ellipsis">{t( 'admin.docs.investmentsDocs' )}</h4>
          </Col>
          <Col sm="auto">
            <Checkbox
              showErrors={false}
              type="switch"
              value={isInvestmentActive}
              onChange={handleToggleInvestment}
              classNames={{
                formGroup: 'm-0 me-1',
              }}
            />
          </Col>
        </Row>

        {data?.[DocRequestTemplateCategoryType.investments]?.length
          ? (
            <DocsTableEdit
              data={data?.[DocRequestTemplateCategoryType.investments]}
              reloadData={reloadData}
              handleViewDocument={handleViewDocument}
            />
          )
          : <h4 className="text--gray12 text-center">{t( 'admin.docs.empty' )}</h4>}

        <BtnCreateRequest
          category={DocRequestTemplateCategoryType.investments}
          reloadData={fetchData}
        />

      </CardLight>
      {showFileModal && currentFileUrl && (
      <PdfViewerModal url={currentFileUrl} onClose={closeFileModal} />
      )}
    </div>
  );
}

export default DocsView;
